import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { toApiError } from "@/lib/api-error";
import { useAuthStore } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/v2/PageHeader";
import { parseDateLocal, formatPreferredDate, formatTimeMaybe } from "@/lib/booking-date";

/**
 * Calendar — Pencil frame `IpDV2`. Month grid with prev/next nav and day cells
 * containing booking previews. Click a row → /bookings/{id}.
 */

export function Calendar() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const monthStart = useMemo(
    () => new Date(cursor.getFullYear(), cursor.getMonth(), 1),
    [cursor],
  );
  const monthEnd = useMemo(
    () => new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1),
    [cursor],
  );

  const list = useQuery({
    queryKey: ["bookings.calendar", monthStart.toISOString()],
    enabled: !!accessToken,
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/bookings", {
        params: {
          query: {
            start: monthStart.toISOString(),
            end: monthEnd.toISOString(),
            size: 200,
          },
        },
      });
      if (error) throw toApiError(error);
      return data;
    },
  });

  // A broad recent set (all bookings, not just the month window). The booking
  // date lives in custom fields (primary-date → preferredDate; "show on
  // calendar" fields → calendarDates), so we bucket from this set rather than a
  // server date-window: a booking whose primary date is in another month may
  // still carry a calendar date that lands in THIS month. size=500 captures the
  // realistic backlog; if a tenant exceeds that, the oldest rows won't show here.
  const recent = useQuery({
    // Distinct from the Dashboard's ["bookings.recent"] key: that query returns
    // the full Page object, this one returns an array (data.content). Sharing a
    // key made React Query serve one shape to the other consumer — the Dashboard's
    // Page object would then hit the `for…of` below and throw "object is not
    // iterable", crashing the page after a workspace switch (which lands on the
    // Dashboard first, seeding the object).
    queryKey: ["bookings.calendar.recent"],
    enabled: !!accessToken,
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/bookings", {
        params: { query: { size: 500 } },
      });
      if (error) throw toApiError(error);
      return data?.content ?? [];
    },
  });

  // Defense-in-depth: only ever iterate an array. `?? []` alone doesn't guard a
  // wrong-shaped (non-null) cached value, so normalize once and use everywhere.
  const recentRows = Array.isArray(recent.data) ? recent.data : [];

  const buckets = useMemo(() => {
    const out = new Map<
      string,
      { count: number; rows: { id: string; label: string; status: string }[] }
    >();
    const keyOf = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    // Guard: a booking is placed at most once per day, so its appointment slot
    // and a same-day "show on calendar" date don't double-count.
    const seen = new Set<string>();
    const add = (key: string, row: { id: string; label: string; status: string }) => {
      const dedup = `${row.id}:${key}`;
      if (seen.has(dedup)) return;
      seen.add(dedup);
      const bucket = out.get(key) ?? { count: 0, rows: [] };
      bucket.count += 1;
      if (bucket.rows.length < 3) bucket.rows.push(row);
      out.set(key, bucket);
    };
    const inMonth = (d: Date | null): d is Date =>
      !!d && d >= monthStart && d < monthEnd;
    const dotted = (...parts: (string | null | undefined)[]) =>
      parts.filter((p) => p && p.trim()).join(" · ");

    // Primary booking dates — driven off `recent` (customer-chosen main date).
    for (const b of recentRows) {
      const pd = (b as { preferredDate?: string | null }).preferredDate;
      if (!pd) continue;
      const d = parseDateLocal(pd);
      if (!inMonth(d)) continue;
      const time = formatTimeMaybe(pd);
      add(keyOf(d), {
        id: b.id ?? "",
        label: dotted(b.serviceName ?? "(service)", time || undefined, b.customerName),
        status: b.status ?? "",
      });
    }
    // Calendar-linked custom dates — any number per booking. A booking lands on
    // each "show on calendar" field's day; the label says which date it is.
    // Driven off `recent` (not the month window) so a scheduled booking whose
    // startsAt is in another month still surfaces its in-month calendar dates.
    // The per-day `seen` guard keeps a same-day slot + calendar-date single.
    for (const b of recentRows) {
      const cds =
        (b as { calendarDates?: { fieldId: string; label: string; date: string }[] })
          .calendarDates ?? [];
      for (const cd of cds) {
        const d = parseDateLocal(cd.date);
        if (!inMonth(d)) continue;
        // "Date and Time" fields carry a time — show it next to the field label.
        const time = formatTimeMaybe(cd.date);
        add(keyOf(d), {
          id: b.id ?? "",
          label: dotted(
            b.serviceName ?? "(service)",
            time ? `${cd.label} ${time}` : cd.label,
            b.customerName,
          ),
          status: b.status ?? "",
        });
      }
    }
    return out;
  }, [recentRows, monthStart, monthEnd]);

  // Strip below the grid: bookings not placed on any calendar day — no
  // confirmed time AND no "show on calendar" date. (Their main date, if any,
  // still shows in the Bookings list; it's intentionally not on the calendar.)
  const offCalendarRows = useMemo(
    () =>
      recentRows.filter((b) => {
        const pd = (b as { preferredDate?: string | null }).preferredDate;
        if (pd) return false; // has a primary date → shown on that day
        const cds = (b as { calendarDates?: unknown[] }).calendarDates ?? [];
        return cds.length === 0;
      }),
    [recentRows],
  );

  const cells = useMemo(() => {
    const start = new Date(monthStart);
    const dayOfWeek = start.getDay();
    start.setDate(start.getDate() - dayOfWeek);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const inMonth = d.getMonth() === monthStart.getMonth();
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      return { date: d, key, inMonth };
    });
  }, [monthStart]);

  const today = new Date();
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  return (
    <div className="px-6 py-6 space-y-6">
      <PageHeader
        title="Calendar"
        subtitle={`${list.data?.totalElements ?? 0} bookings in ${monthStart.toLocaleString("en-US", {
          month: "long",
          year: "numeric",
        })}`}
        actions={
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1))}
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                const d = new Date();
                setCursor(new Date(d.getFullYear(), d.getMonth(), 1));
              }}
            >
              Today
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1))}
              aria-label="Next month"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        }
      />

      {list.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {list.isError && <p className="text-sm text-destructive">{(list.error as Error).message}</p>}

      {/* Not on the calendar — bookings with no confirmed time and no
          "show on calendar" date. Their main date (if any) is shown here and in
          the Bookings list; click through and use Reschedule to assign a time. */}
      {offCalendarRows.length > 0 && (
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium">Not on the calendar</h3>
            <span className="text-[11px] text-muted-foreground">
              {offCalendarRows.length} not scheduled on a day
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {offCalendarRows.map((b) => {
              const mainDate = (b as { preferredDate?: string | null }).preferredDate;
              return (
                <Link
                  key={b.id}
                  to={`/bookings?focus=${b.id}`}
                  className="rounded-md border px-2.5 py-1.5 text-xs hover:bg-accent"
                  title={`${b.serviceName ?? "(service)"} · ${b.customerName ?? ""}`}
                >
                  <span className="font-medium">{b.serviceName ?? "(service)"}</span>
                  {b.customerName ? (
                    <span className="text-muted-foreground"> · {b.customerName}</span>
                  ) : null}
                  {mainDate ? (
                    <span className="text-muted-foreground"> · {formatPreferredDate(mainDate)}</span>
                  ) : null}
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <div className="rounded-xl border bg-card overflow-hidden">
        <div className="grid grid-cols-7 border-b border-border bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="px-2 py-2 text-center">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((cell, i) => {
            const bucket = buckets.get(cell.key);
            const dim = !cell.inMonth;
            const isToday = cell.key === todayKey;
            return (
              <div
                key={i}
                className={`min-h-[110px] border-b border-r border-border p-2 text-xs ${
                  dim ? "bg-muted/30" : ""
                }`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`text-xs ${
                      isToday
                        ? "rounded-full bg-primary text-primary-foreground px-1.5 py-0.5"
                        : dim
                          ? "text-muted-foreground/60"
                          : ""
                    }`}
                  >
                    {cell.date.getDate()}
                  </span>
                  {bucket && (
                    <span className="text-[10px] text-muted-foreground">{bucket.count}</span>
                  )}
                </div>
                {bucket && (
                  <ul className="mt-1 space-y-0.5">
                    {bucket.rows.map((r, ri) => (
                      <li key={`${r.id}-${ri}`} className="truncate">
                        <Link
                          to={`/bookings?focus=${r.id}`}
                          className={`block truncate rounded px-1 hover:bg-accent ${
                            r.status === "completed" || r.status === "confirmed"
                              ? "text-status-completed"
                              : r.status === "cancelled" || r.status === "canceled" || r.status === "no_show"
                                ? "text-status-cancelled line-through"
                                : "text-foreground"
                          }`}
                          title={`${r.label} (${r.status})`}
                        >
                          {r.label}
                        </Link>
                      </li>
                    ))}
                    {bucket.count > 3 && (
                      <li className="text-muted-foreground text-[10px] pl-1">
                        +{bucket.count - 3} more
                      </li>
                    )}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
